import { CompilationStatus, ShareGroupVideoAudiences, } from "@memento/types/compilation";
import { isVideoSegment, } from "@memento/types/segment";
import { getCompositionDurationInSeconds, getClipSeriesDurationSeconds, } from "./render/index.js";
import { getPluralEnding } from "./text.js";
import { getSegmentDuration, segmentToRenderConfigClip } from "./segment.js";
import { assertNever, isObject } from "./type-witchcraft.js";
// statuses indicating that the compilation is still in progress
export const inProgressCompilationV3Statuses = [
    CompilationStatus.RENDERING,
    CompilationStatus.PREPARING,
    CompilationStatus.STITCHING,
];
// statuses indicating that the compilation is done (whether successful or not)
const compilationDoneStatuses = [
    CompilationStatus.FAILED,
    CompilationStatus.COMPLETE,
    CompilationStatus.ARCHIVED,
];
// statuses indicating that the compilation is still in progress
export const inProgressCompilationV2Statuses = [
    ...inProgressCompilationV3Statuses,
    CompilationStatus.CREATED,
];
export function getIsCompilationInProgress(compilation) {
    if (isCompilationV1OrV2(compilation)) {
        return inProgressCompilationV2Statuses.includes(compilation.compilationStatus);
    }
    else {
        return inProgressCompilationV3Statuses.includes(compilation.compilationStatus);
    }
}
export function getIsCompilationStarted(compilationStatus) {
    // anything after creating the compilation should be considered started
    return compilationStatus !== CompilationStatus.CREATED;
}
export function getIsCompilationDone(compilationStatus) {
    return compilationDoneStatuses.includes(compilationStatus);
}
export function isShareGroupVideoAudience(value) {
    return ShareGroupVideoAudiences.includes(value);
}
/**
 * This function compares two compilation objects and tells you whether the "my"
 * compilation is out of date compared to the "database" compilation.
 */
export function getIsMyCompilationStale(databaseCompilationInfo, myCompilationInfo, { strategy = "equal-sent-at-is-stale" } = {}) {
    // if there is no database save request, myCompilation cannot be stale
    if (!databaseCompilationInfo) {
        return false;
    }
    // if myCompilation is undefined, it is stale
    else if (!myCompilationInfo) {
        return true;
    }
    // myCompilation is stale if its sequence number is less than the database's
    else if (myCompilationInfo.sequenceNumber < databaseCompilationInfo.sequenceNumber) {
        return true;
    }
    else if (myCompilationInfo.sequenceNumber === databaseCompilationInfo.sequenceNumber) {
        if (myCompilationInfo.sentAt < databaseCompilationInfo.sentAt) {
            return true;
        }
        else if (myCompilationInfo.sentAt === databaseCompilationInfo.sentAt) {
            if (strategy === "equal-sent-at-is-stale") {
                return true;
            }
            else {
                return false;
            }
        }
        else {
            return false;
        }
    }
    else {
        return false;
    }
}
export function isCompilationV1(compilation) {
    if (!isObject(compilation)) {
        return false;
    }
    else if (!("version" in compilation) ||
        compilation.version === 1 ||
        compilation.version === undefined) {
        return true;
    }
    else {
        return false;
    }
}
export function isCompilationV1Database(compilationDatabase) {
    return isCompilationV1(compilationDatabase);
}
export function isCompilationV2(compilation) {
    if (!isObject(compilation)) {
        return false;
    }
    else {
        return compilation.version === 2;
    }
}
export function isCompilationV2Database(compilationDatabase) {
    return isCompilationV2(compilationDatabase);
}
export function isCompilationV1OrV2(compilation) {
    return isCompilationV2(compilation) || isCompilationV1(compilation);
}
export function isCompilationV1OrV2Database(compilationDatabase) {
    return (isCompilationV2Database(compilationDatabase) ||
        isCompilationV1Database(compilationDatabase));
}
export function isCompilationV3(compilation) {
    if (!isObject(compilation)) {
        return false;
    }
    else {
        return compilation.version === 3;
    }
}
export function isCompilationV3Database(compilationDatabase) {
    return isCompilationV3(compilationDatabase);
}
function formatCompilationDuration(compilationDurationSeconds, renderTimeElapsedMinutes, hasSegments) {
    const compilationDurationMinutes = Math.floor(compilationDurationSeconds / 60);
    const remainingCompilationSeconds = compilationDurationSeconds - compilationDurationMinutes * 60;
    const compilationDurationString = hasSegments
        ? `${compilationDurationMinutes > 0
            ? compilationDurationMinutes + " min"
            : ""} ${Math.round(remainingCompilationSeconds).toLocaleString("en-US", {
            minimumIntegerDigits: 1,
            useGrouping: false,
        })} sec`
        : "(calculating...)";
    const compilationDurationStringVerbose = hasSegments
        ? [
            compilationDurationMinutes > 0
                ? compilationDurationMinutes +
                    ` minute${getPluralEnding(compilationDurationMinutes)}`
                : "",
            `${Math.round(remainingCompilationSeconds).toLocaleString("en-US", {
                minimumIntegerDigits: 1,
                useGrouping: false,
            })} second${getPluralEnding(remainingCompilationSeconds)}`,
        ]
            .filter(Boolean)
            .join(" ")
        : "(calculating...)";
    const renderEstimatedDurationMinutes = hasSegments
        ? // When this calculation shakes out, it ends up being the duration of the compilation
            // in minutes, doubled, rounded to the nearest 5 seconds, plus 10 minutes.
            // So if the compilation is 330 seconds long, the renderEstimatedDurationMinutes would be 21.
            Math.max(10, Math.round((compilationDurationSeconds * 2) / 300) * 5 + 10)
        : 0;
    const renderEstimatedDurationMinutesString = renderEstimatedDurationMinutes > 0
        ? renderEstimatedDurationMinutes
        : "(calculating...)";
    const renderEstimatedDurationRemainingMinutes = Math.ceil(Math.max(renderEstimatedDurationMinutes - renderTimeElapsedMinutes, 0) / 5) * 5;
    const renderEstimatedDurationRemainingString = renderEstimatedDurationRemainingMinutes > 0
        ? `${renderEstimatedDurationRemainingMinutes}`
        : "a few";
    return {
        compilationDurationSeconds,
        compilationDurationMinutes,
        compilationDurationString,
        compilationDurationStringVerbose,
        renderEstimatedDurationMinutes,
        renderEstimatedDurationMinutesString,
        renderTimeElapsedMinutes,
        renderEstimatedDurationRemainingMinutes,
        renderEstimatedDurationRemainingString,
    };
}
export function getCompilationDurationsV2(segments, compilation) {
    const hasSegments = segments && segments.length > 0;
    const compilationDurationSeconds = hasSegments
        ? Math.round(segments.reduce((sum, segment) => {
            const segmentDuration = segment?.edit?.cutInfo?.cut
                ? getSegmentDuration(segment) *
                    (segment.edit.cutInfo.end - segment.edit.cutInfo.start)
                : getSegmentDuration(segment);
            return sum + segmentDuration;
        }, 0))
        : 0;
    const renderTimeElapsedMinutes = compilation
        ? Math.round((Date.now() - compilation.createdAt) / (1000 * 60))
        : 0;
    return formatCompilationDuration(compilationDurationSeconds, renderTimeElapsedMinutes, hasSegments);
}
export function getCompilationDurationsFromClipsV3(clips, compilation, theme) {
    const fps = compilation?.renderRequest.renderConfig.videoFrame.fps || 30;
    const compilationDurationSeconds = compilation?.renderRequest.renderConfig && theme
        ? getCompositionDurationInSeconds({
            ...compilation.renderRequest.renderConfig,
            clips,
        }, theme)
        : getClipSeriesDurationSeconds({
            clips,
            fps,
            theme,
            scene: { type: "clip-series-basic" },
        });
    const renderStartedAt = compilation?.renderRequest.status.startedAt;
    const renderTimeElapsedMinutes = renderStartedAt
        ? Math.round((Date.now() - renderStartedAt) / (1000 * 60))
        : 0;
    const hasSegments = clips.length > 0;
    return formatCompilationDuration(compilationDurationSeconds, renderTimeElapsedMinutes, hasSegments);
}
export function getCompilationDurationsV3(activeSegments, compilation, theme) {
    const clips = activeSegments.map(segmentToRenderConfigClip);
    return getCompilationDurationsFromClipsV3(clips, compilation, theme);
}
export function getIsCompilationStuck(activeSegments, compilation, themes, 
// how much beyond the estimated render time the compilation render must be to
// be considered stuck
overEstimateStuckMinutes) {
    const { renderEstimatedDurationMinutes, renderTimeElapsedMinutes } = !compilation || isCompilationV3(compilation)
        ? getCompilationDurationsV3(activeSegments, compilation, themes.find((theme) => theme.themeId === compilation?.renderRequest.renderConfig.themeId))
        : getCompilationDurationsV2(activeSegments.filter(isVideoSegment), compilation);
    const isCompilationInProgress = compilation
        ? getIsCompilationInProgress(compilation)
        : false;
    const isCompilationStuck = isCompilationInProgress &&
        renderTimeElapsedMinutes > 0 &&
        renderEstimatedDurationMinutes > 0 &&
        renderTimeElapsedMinutes - renderEstimatedDurationMinutes >
            overEstimateStuckMinutes;
    return isCompilationStuck;
}
/** Returns true if this card has any compilations that are in progress and not
 * stuck.
 */
export function getIsAnyCompilationInProgress(allCompilations, segments, themes, 
// how much beyond the estimated render time the compilation render must be to
// be considered stuck
overEstimateStuckMinutes) {
    const activeSegments = segments.filter((segment) => !segment.isDeleted);
    return allCompilations.some((compilation) => {
        // not in progress, no need to continue
        if (!getIsCompilationInProgress(compilation)) {
            return false;
        }
        const isStuck = getIsCompilationStuck(activeSegments, compilation, themes, overEstimateStuckMinutes);
        // is stuck, so doesn't count as "in progress"
        if (isStuck) {
            return false;
        }
        return true;
    });
}
export function getUserInputTextCharacterCountMessage(field, value) {
    const length = value?.length || 0;
    return `${length} / ${field.max} character${getPluralEnding(field.max)}`;
}
export function validateUserInputField(field, value) {
    const type = field.type;
    const required = Boolean(field.required);
    // all fields can be required
    if (required && !value) {
        return {
            valid: false,
            message: "Required",
        };
    }
    if (type === "text" || type === "textarea") {
        const length = value?.length || 0;
        if (length > field.max) {
            return {
                valid: false,
                message: getUserInputTextCharacterCountMessage(field, value),
            };
        }
        else if (length < field.min) {
            return {
                valid: false,
                message: field.min === 1 ? "Required" : `Minimum ${field.min} characters`,
            };
        }
        else {
            return { valid: true };
        }
    }
    else if (type === "number") {
        // at this point we know the value isn't required, so if it doesn't have a
        // min or max, we can just return valid
        if (field.min === undefined && field.max === undefined) {
            return { valid: true };
        }
        // if the value is undefined, then we can't check min/max, so consider it
        // valid
        else if (value === undefined) {
            return { valid: true };
        }
        const valueNumber = Number(value);
        if (isNaN(valueNumber)) {
            return {
                valid: false,
                message: "Invalid number",
            };
        }
        if (field.min !== undefined && valueNumber < field.min) {
            return {
                valid: false,
                message: `Minimum ${field.min}`,
            };
        }
        else if (field.max !== undefined && valueNumber > field.max) {
            return {
                valid: false,
                message: `Maximum ${field.max}`,
            };
        }
        else {
            return { valid: true };
        }
    }
    else if (type === "date") {
        // we already made the required check, which is the only one we need to make
        return { valid: true };
    }
    else if (type === "colorPicker") {
        // we already made the required check, which is the only one we need to make
        return { valid: true };
    }
    else {
        assertNever(type);
    }
}
/** Checks if all required user input fields have values. */
export function getIsRenderConfigUserInputComplete(renderConfig, theme) {
    const userInputFields = theme?.userInputFields ?? [];
    const userInput = renderConfig.userInput;
    const isThemeUserInputComplete = userInputFields.every((field) => {
        const value = userInput?.[field.id];
        return validateUserInputField(field, value).valid;
    });
    return isThemeUserInputComplete;
}
/** Checks to see if any compilation for a card is currently rendering */
export function getIsAnyCompilationRendering(allCompilations, segments, themes, 
// how much beyond the estimated render time the compilation render must be to
// be considered stuck
overEstimateStuckMinutes) {
    return allCompilations?.some((compilation) => {
        return (getIsCompilationInProgress(compilation) &&
            !getIsCompilationStuck(segments, compilation, themes, overEstimateStuckMinutes));
    });
}
export function getIsRecentCompilationComplete(allCompilations, segments, themes, 
// how much beyond the estimated render time the compilation render must be to
// be considered stuck
overEstimateStuckMinutes) {
    const isAnyCompilationRendering = getIsAnyCompilationRendering(allCompilations, segments, themes, overEstimateStuckMinutes);
    // Normally we only care about the most recent compilation, but if that compilation
    // was just created, we'll consider the second most recent compilation instead.
    // First, sort to put the later compilations first
    const sortedCompilations = allCompilations?.sort((a, b) => b.createdAt - a.createdAt);
    // Grab the first, meaning most recent compilation
    let recentCompilation = sortedCompilations?.[0];
    if (recentCompilation?.compilationStatus === CompilationStatus.CREATED) {
        recentCompilation = sortedCompilations?.[1] || recentCompilation;
    }
    // We only show the complete state if there isn't a compilation currently rendering
    const isCompilationComplete = recentCompilation?.compilationStatus === CompilationStatus.COMPLETE &&
        !isAnyCompilationRendering;
    return isCompilationComplete;
}
